import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { queryKeys } from "../../api/queryKeys";
import {
  myProjectsSchema,
  projectAttributionRequestItemSchema,
  projectAttributionRequestSchema,
  projectDaysSchema,
  projectPeriodLockSchema,
} from "../../api/schemas";
import type {
  AttributionRequestBody,
  MyProjects,
  ProjectAttributionRequest,
  ProjectAttributionRequestItem,
  ProjectDays,
  ProjectPeriodLock,
} from "../../api/schemas";
import type { LocalDate } from "../../domain/time";

/**
 * Loads the project view of the own working days. The key lies below `["me"]`, which every
 * successful synchronization invalidates, so attributions sent from this device appear as the
 * server resolved them.
 *
 * @param from - First local date.
 * @param to - Last local date (at most 93 days after `from`).
 * @returns The query result.
 */
export function useProjectDays(from: LocalDate, to: LocalDate): UseQueryResult<ProjectDays> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.projectDays(from, to),
    queryFn: ({ signal }) =>
      client.get("/api/v1/me/project-days", projectDaysSchema, { query: { from, to }, signal }),
    // The range follows today: at midnight an open day editor stays with its draft (F-15).
    placeholderData: keepPreviousData,
  });

  return query;
}

/**
 * Loads the projects the person may choose on a day (F-24). The server accepts an attribution only
 * for a project selectable on its own day, so a past day must not offer the projects of today (for
 * example a membership that starts today).
 *
 * @param date - The local date of the working day.
 * @param enabled - Whether to load; off while offline.
 * @returns The query result.
 */
export function useMyProjectsOn(date: LocalDate, enabled: boolean): UseQueryResult<MyProjects> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.myProjectsOn(date),
    queryFn: ({ signal }) =>
      client.get("/api/v1/me/projects", myProjectsSchema, { query: { on: date }, signal }),
    enabled,
    staleTime: 60_000,
  });

  return query;
}

/**
 * Loads the locked project periods in force (they hold no personal data, every employee sees them).
 *
 * @param enabled - Whether project time tracking is available.
 * @returns The query result.
 */
export function useProjectPeriodLocks(enabled: boolean): UseQueryResult<ProjectPeriodLock[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.projectPeriodLocks,
    queryFn: ({ signal }) =>
      client.get("/api/v1/project-period-locks", projectPeriodLockSchema.array(), { signal }),
    enabled,
    staleTime: 60_000,
  });

  return query;
}

/**
 * Loads the visible requests for attributions in locked periods: the own ones and, for HR and
 * project responsibles, those they may decide.
 *
 * @param scope - `pending` for undecided requests only, `all` for every state.
 * @param enabled - Whether to load them.
 * @returns The query result.
 */
export function useAttributionRequests(
  scope: "all" | "pending",
  enabled = true,
): UseQueryResult<ProjectAttributionRequestItem[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.attributionRequests(scope),
    queryFn: ({ signal }) =>
      client.get(
        "/api/v1/project-attribution-requests",
        projectAttributionRequestItemSchema.array(),
        {
          query: scope === "pending" ? { pendingOnly: "true" } : {},
          signal,
        },
      ),
    enabled,
  });

  return query;
}

/** Body of `POST /project-attribution-requests`. */
export interface AttributionRequestInput {
  /** The requested attribution; its identifier is kept on approval. */
  readonly attribution: AttributionRequestBody;
  readonly reason: string;
}

/**
 * Requests an attribution in a locked period (needs a connection, `PINV-P05`).
 *
 * @returns The mutation.
 */
export function useCreateAttributionRequest(): UseMutationResult<
  ProjectAttributionRequest,
  Error,
  AttributionRequestInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: AttributionRequestInput) =>
      client.post("/api/v1/project-attribution-requests", input, projectAttributionRequestSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.allAttributionRequests }),
  });

  return mutation;
}

/** Input of a decision about a request. */
export interface AttributionDecisionInput {
  readonly id: string;
  readonly approve: boolean;
  readonly note: string | null;
}

/**
 * Approves or rejects a request; on approval the attribution is stored despite the lock.
 *
 * @returns The mutation.
 */
export function useDecideAttributionRequest(): UseMutationResult<
  ProjectAttributionRequest,
  Error,
  AttributionDecisionInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: AttributionDecisionInput) =>
      client.post(
        `/api/v1/project-attribution-requests/${encodeURIComponent(input.id)}:${input.approve ? "approve" : "reject"}`,
        { note: input.note },
        projectAttributionRequestSchema,
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.allAttributionRequests }),
        queryClient.invalidateQueries({ queryKey: queryKeys.allProjectDays }),
      ]),
  });

  return mutation;
}
